<template>
	<div class="flex flex-col max-w-full min-w-0 mb-16">
		<div class="grid grid-cols-[1fr_300px] gap-4 py-4">
			<div class="flex flex-col gap-4">
				<AppAddressInput v-model="address" class="w-full" />

				<img
					v-if="imageUrl"
					:src="imageUrl"
					class="w-full aspect-3/2 object-cover"
					referrerpolicy="no-referrer"
				/>
				<div v-else-if="address" class="w-full aspect-3/2 object-cover" />
			</div>

			<div class="flex flex-col gap-2 border-l px-4 py-2">
				<span v-if="addressMeta" class="text-sm">
					{{
						hasSomeSources
							? "Dit huis hebben we gevonden in de volgende bronnen:"
							: "Dit huis is niet gevonden in onze bronnen"
					}}
				</span>

				<template v-if="addressMeta">
					<AppButton
						v-if="mostRecentListing"
						as="a"
						variant="secondary"
						icon-right="open_in_new"
						class="justify-between"
						:href="mostRecentListing.funda_url"
						target="_blank"
						rel="noreferrer"
					>
						<span class="flex items-center gap-1">
							<img src="/logos/funda_ico.svg" class="w-4 h-4" />
							<span>Funda</span>
						</span>
					</AppButton>

					<AppButton
						v-if="addressMeta?.address && address"
						as="a"
						variant="secondary"
						icon-right="open_in_new"
						class="justify-between"
						:href="`https://www.kadaster.nl/productoverzicht-adres?huisnummer=${getHuisnummer(address)}&postcode=${address.postcode}&plaatsnaam=${address.woonplaatsNaam}&straatnaam=${address.openbareRuimteNaam}`"
						target="_blank"
						rel="noreferrer"
					>
						<span class="flex items-center gap-1">
							<img src="/logos/kadaster_ico.svg" class="w-4 h-4" />
							<span>Kadaster</span>
						</span>
					</AppButton>

					<div
						v-if="sortedEnergyLabels.length > 0"
						class="inline-flex items-center justify-start rounded-sm border border-[var(--border)] bg-white px-2.5 py-1.5 text-sm font-semibold text-[var(--text-primary)]"
					>
						<span class="flex items-center gap-1">
							<img src="/logos/rijksoverheid_ico.svg" class="w-4 h-4" />
							<span>EP-online</span>
						</span>
					</div>
					<div
						v-if="addressMeta?.wozWaarden"
						class="inline-flex items-center justify-start rounded-sm border border-[var(--border)] bg-white px-2.5 py-1.5 text-sm font-semibold text-[var(--text-primary)]"
					>
						<span class="flex items-center gap-1">
							<img src="/logos/woz_ico.svg" class="w-4 h-4" />
							<span>WOZ-waardeloket</span>
						</span>
					</div>

					<AppButton
						v-if="hasSomeSources"
						variant="secondary"
						icon="keyboard_return"
						class="mt-4"
						@click="applyAllDataSources"
					>
						Gevonden gegevens toepassen
					</AppButton>
				</template>
			</div>
		</div>

		<div v-if="address" class="flex flex-col pb-2">
			<h2>{{ addressLine5(address) }}</h2>
			<span class="text-sm text-gray-500">{{ addressLine6(address) }}</span>
		</div>

		<div class="flex flex-col gap-4">
			<AppCard title="Aankoop">
				<PropertyList>
					<PropertyInput
						v-if="isPurchase"
						v-model="dossierDataStore.data.objectData.contingencyDate"
						label="Ontbindende voorwaarden tot"
						:value-type="ValueType.Date"
						input-class="w-[200px]"
						dd-class="text-sm"
					/>

					<PropertyRow
						v-if="mostRecentListing"
						label="Vraagprijs"
						dd-class="text-lg font-semibold"
					>
						<template #actions>
							<img src="/logos/funda_ico.svg" class="w-4 h-4" />
						</template>
						{{ mostRecentListing.funda_price_str }}
					</PropertyRow>

					<PropertyField v-model="koopsom" label="Koopsom" dd-class="text-sm">
						<template #sources>
							<DataSource
								source="funda"
								:value="mostRecentListing?.funda_price_num"
							/>
						</template>
						<AppFieldNumeric
							v-model="koopsom"
							:value-type="ValueType.Currency"
							:precision="0"
							:grouping="true"
							prefix="€"
							wrapper-class="w-[200px]"
						/>
					</PropertyField>
				</PropertyList>
			</AppCard>

			<AppCard title="Object">
				<PropertyList>
					<PropertyField
						v-model="dossierDataStore.data.objectData.buildType"
						label="Bouwsoort"
					>
						<template #sources>
							<DataSource
								source="funda"
								:value="fundaBuildType"
								:transformer="transformBuildType"
							/>
						</template>
						<OptionInput
							v-model="dossierDataStore.data.objectData.buildType"
							class="text-sm"
							:options="[
								{ value: 'existing', label: 'Bestaande bouw' },
								{ value: 'new', label: 'Nieuwbouw' },
							]"
						/>
					</PropertyField>

					<PropertyField
						v-if="dossierDataStore.data.objectData.buildType === 'new'"
						v-model="dossierDataStore.data.objectData.soortNieuwbouw"
						label="Soort nieuwbouw"
					>
						<DropdownInput
							v-model="dossierDataStore.data.objectData.soortNieuwbouw"
							class="text-sm"
							:options="[
								{ value: '', label: 'Selecteren...' },
								...hdnListToOptions(SoortNieuwbouwType, {
									shouldSort: true,
								}),
							]"
						/>
					</PropertyField>

					<PropertyInput
						v-if="dossierDataStore.data.objectData.buildType === 'new'"
						v-model="dossierDataStore.data.objectData.opleveringsDatum"
						label="Opleveringsdatum"
						:value-type="ValueType.Date"
						input-class="w-[200px]"
						dd-class="text-sm"
					/>

					<PropertyRow
						v-if="dossierDataStore.data.objectData.buildType === 'new'"
						label="Hypotheek incl. renteverlies?"
					>
						<OptionInput
							v-model="
								dossierDataStore.data.objectData.hypotheekInclRenteVerlies
							"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyRow>

					<PropertyField
						v-model="dossierDataStore.data.objectData.objectType"
						label="Objectsoort"
					>
						<template #sources>
							<DataSource
								source="funda"
								:value="fundaObjectType"
								:transformer="transformObjectType"
							/>
						</template>
						<DropdownInput
							v-model="dossierDataStore.data.objectData.objectType"
							class="text-sm"
							:options="[
								{ value: '', label: 'Selecteren...' },
								...hdnListToOptions(ObjectSoortType, {
									shouldSort: true,
								}),
							]"
						/>
					</PropertyField>

					<PropertyField
						v-model="dossierDataStore.data.objectData.garagePresent"
						label="Garage"
					>
						<template #sources>
							<DataSource source="funda" :value="fundaGaragePresent" />
						</template>
						<OptionInput
							v-model="dossierDataStore.data.objectData.garagePresent"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyField>

					<PropertyRow label="Wordt het object verhuurd?">
						<OptionInput
							v-model="dossierDataStore.data.objectData.isRented"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyRow>

					<PropertyRow
						v-if="dossierDataStore.data.objectData.isRented"
						label="Soort verhuur"
					>
						<DropdownInput
							v-model="dossierDataStore.data.objectData.rentalType"
							class="text-sm"
							:options="[
								{ value: '', label: 'Selecteren...' },
								...hdnListToOptions(VerhuurType, {
									shouldSort: true,
								}),
							]"
						/>
					</PropertyRow>

					<PropertyRow label="Verduurzaming besproken?">
						<OptionInput
							v-model="dossierDataStore.data.objectData.verduurzamingBesproken"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyRow>

					<PropertyField
						v-model="dossierDataStore.data.objectData.bouwjaar"
						label="Bouwjaar"
						dd-class="text-sm"
					>
						<template #sources>
							<DataSource
								source="kadaster"
								:value="kadasterBouwjaar"
								:transformer="transformBouwjaar"
							/>
							<DataSource
								source="epOnline"
								:value="epOnlineBouwjaar"
								:transformer="transformBouwjaar"
							/>
							<DataSource
								source="funda"
								:value="fundaBouwjaar"
								:transformer="transformBouwjaar"
							/>
						</template>
						<InputText
							v-model="dossierDataStore.data.objectData.bouwjaar"
							class="w-[200px]"
						/>
					</PropertyField>
				</PropertyList>
			</AppCard>

			<AppCard title="Energie">
				<PropertyList>
					<PropertyField
						v-model="dossierDataStore.data.objectData.energielabel"
						label="Energielabel"
					>
						<template #sources>
							<DataSource
								source="epOnline"
								:value="epOnlineEnergyLabel"
								:transformer="transformEnergyLabel"
							/>
							<DataSource
								source="funda"
								:value="fundaEnergyLabel"
								:transformer="transformEnergyLabel"
							/>
						</template>
						<EnergyLabelSelect
							v-model="dossierDataStore.data.objectData.energielabel"
						/>
					</PropertyField>

					<PropertyField
						v-model="dossierDataStore.data.objectData.energielabelAfgifteDate"
						label="Afgiftedatum energielabel"
						dd-class="text-sm"
					>
						<template #sources>
							<DataSource
								source="epOnline"
								:value="epOnlineRegistratiedatum"
								:transformer="transformDate"
							/>
						</template>
						<AppFieldDate
							v-model="dossierDataStore.data.objectData.energielabelAfgifteDate"
							class="w-[250px]"
						/>
					</PropertyField>
				</PropertyList>
			</AppCard>

			<AppCard title="Waarde">
				<PropertyList>
					<PropertyInput
						v-model="dossierDataStore.data.objectData.marktwaardeVoorVerbouwing"
						label="Marktwaarde (voor verbouwing)"
						:value-type="ValueType.Currency"
						input-class="w-[200px]"
						dd-class="text-sm"
						:precision="0"
					/>

					<PropertyInput
						v-model="dossierDataStore.data.objectData.marktwaarde"
						label="Marktwaarde (na verbouwing)"
						:value-type="ValueType.Currency"
						input-class="w-[200px]"
						dd-class="text-sm"
						:precision="0"
						:data-sources="marktwaardeDataSources"
					/>

					<PropertyField
						v-model="dossierDataStore.data.objectData.bronWaardebepaling"
						label="Bron waardebepaling"
					>
						<DropdownInput
							v-model="dossierDataStore.data.objectData.bronWaardebepaling"
							class="text-sm"
							:options="[
								{ value: '', label: 'Selecteren...' },
								...hdnListToOptions(BronWaardebepalingType, {
									shouldSort: true,
								}),
							]"
						/>
					</PropertyField>

					<PropertyRow
						v-if="addressMeta?.wozWaarden"
						label="WOZ-waarde"
						dd-class="text-sm"
					>
						<template #actions>
							<DataSourceButton
								v-if="addressMeta?.wozWaarden?.[0]"
								:value="addressMeta.wozWaarden[0].vastgesteldeWaarde"
								:current-value="dossierDataStore.data.objectData.wozWaarde"
								source="wozWaardeLoket"
								:on-apply="applyWozFromAddressMeta"
							/>
						</template>
						<table class="w-full">
							<thead>
								<tr class="text-left">
									<th>Peildatum</th>
									<th>WOZ-waarde</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="woz in addressMeta.wozWaarden.slice(
										0,
										showAllWoz ? addressMeta.wozWaarden.length : 3,
									)"
									:key="woz.peildatum"
								>
									<td>{{ simpleDate(woz.peildatum) }}</td>
									<td>{{ asDisplayCur(woz.vastgesteldeWaarde) }}</td>
								</tr>
							</tbody>
						</table>
						<AppButton
							variant="link"
							class="self-start"
							@click="showAllWoz = !showAllWoz"
						>
							{{ showAllWoz ? "< Minder weergeven" : "> Alles weergeven" }}
						</AppButton>
					</PropertyRow>

					<template v-else>
						<PropertyInput
							v-model="dossierDataStore.data.objectData.wozWaarde"
							label="WOZ-waarde"
							:value-type="ValueType.Currency"
							input-class="w-[200px]"
							dd-class="text-sm"
							:precision="0"
						/>

						<PropertyInput
							v-model="dossierDataStore.data.objectData.wozWaardeTaxatieDate"
							label="WOZ-waarde taxatiedatum"
							:value-type="ValueType.Date"
							input-class="w-[200px]"
							dd-class="text-sm"
						/>
					</template>

					<PropertyRow
						v-if="dossierDataStore.data.objectData.buildType !== 'new'"
						label="Kosten koper?"
					>
						<OptionInput
							v-model="dossierDataStore.data.objectData.kostenKoper"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyRow>
				</PropertyList>
			</AppCard>

			<AppCard
				v-if="dossierDataStore.data.objectData.erfpacht"
				title="Erfpacht"
			>
				<template #actions>
					<DataSourceButton
						:value="fundaErfpacht"
						:current-value="dossierDataStore.data.objectData.erfpacht"
						source="funda"
						:on-apply="applyErfpacht"
					/>
				</template>

				<template #subtitle>
					<AppButton
						variant="ghost"
						icon="delete"
						title="Erfpacht verwijderen"
						aria-label="Erfpacht verwijderen"
						@click="dossierDataStore.data.objectData.erfpacht = undefined"
					/>
				</template>

				<PropertyList>
					<ErfpachtDetails />
				</PropertyList>
			</AppCard>
			<AppButton
				v-else
				variant="ghost"
				icon="add"
				class="border border-dashed rounded p-4 text-gray-600 hover:bg-gray-200"
				@click="dossierDataStore.data.objectData.erfpacht = {}"
			>
				Erfpacht toevoegen
			</AppButton>
		</div>

		<pre v-if="showValues">{{
			stringifyObject(dossierDataStore.data.objectData)
		}}</pre>
		<pre v-if="showValues">{{ stringifyObject(fundaProperties) }}</pre>
	</div>
</template>

<script setup lang="ts">
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { computed, inject, ref, watch } from "vue";
import { ValueType } from "@/enums/ValueType";
import { asDisplayCur } from "@/helpers/inputHelpers";
import type {
	AddressResponse,
	EnergieLabel,
	Erfpacht,
} from "@loanlink-nl/model-app";
import {
	addressLine5,
	addressLine6,
	simpleDate,
	getHuisnummer,
	addressToLabel,
	stringifyObject,
} from "@/helpers/uiHelpers";
import { api } from "@/loanLinkApi";
import { mapFundaType } from "@loanlink-nl/model-mapping";
import { hdnListToOptions } from "@/helpers/hdnHelpers";
import {
	BronWaardebepalingType,
	ObjectSoortType,
	SoortNieuwbouwType,
	VerhuurType,
} from "@loanlink-nl/model-hdn/25.0/OfferteAanvraag";
import { yesNoOptions } from "@/models/inventarisation/income/loondienstOptions";
import { getErfpacht } from "./useErfpacht";
import type { DataSource } from "./dataSources";
import { fuzzyDateParse } from "@loanlink-nl/migration";
import { DateTime } from "luxon";

const showValues = inject("DEBUG_SHOW_MODELVALUES", false);

const dossierDataStore = useDossierDataStore();
const showAllWoz = ref(false);
const isPurchase = computed(() =>
	["Koop", "Doorstromen", "Complex"].includes(
		dossierDataStore.data.financieringsOpzet.situatie ?? "",
	),
);

const koopsom = computed({
	get: () => dossierDataStore.data.financieringsOpzet.aankoopkosten?.koopsom,
	set: (value) => {
		dossierDataStore.data.financieringsOpzet.aankoopkosten ??= {};
		dossierDataStore.data.financieringsOpzet.aankoopkosten.koopsom = value;
	},
});

const props = defineProps<{
	addressMeta: any;
}>();

const address = defineModel<AddressResponse>({});

watch(
	address,
	async () => {
		if (
			address.value &&
			!address.value?.manualAddress &&
			!address.value?.adresseerbaarObjectIdentificatie
		) {
			const q = addressToLabel(address.value);
			if (q === "") {
				return;
			}

			const { data } = await api.address.getAddress({
				q,
			});

			if (!data.result || data.result?.length === 0) {
				address.value.manualAddress = true;
				return;
			}

			if (data.result.length === 1) {
				address.value = data.result[0];
				return;
			}

			document.getElementById("address-input")?.focus();
		}
	},
	{ immediate: true },
);

function getMostRecentListing(addressMeta: any) {
	if (!addressMeta?.listings) {
		return null;
	}

	return addressMeta.listings?.toSorted((a: any, b: any) =>
		DateTime.fromISO(b.timestamp)
			.diff(DateTime.fromISO(a.timestamp))
			.toMillis(),
	)[0];
}

const mostRecentListing = computed(() => {
	return getMostRecentListing(props.addressMeta);
});

const fundaProperties = computed(() => {
	if (!mostRecentListing.value?.funda_properties) {
		return;
	}
	return JSON.parse(mostRecentListing.value?.funda_properties);
});

const mapEnergyLabelToValue: Record<string, EnergieLabel> = {
	A: "03 A",
	B: "04 B",
	C: "05 C",
	D: "06 D",
	E: "07 E",
	F: "08 F",
	G: "09 G",
	"A+": "02 A+",
	"A++": "01 A++",
	"A+++": "10 A+++",
	"A++++": "11 A++++",
	A1: "02 A+",
	A2: "01 A++",
	A3: "10 A+++",
	A4: "11 A++++",
};

const mapBuildTypeToValue: Record<string, "new" | "existing"> = {
	"bestaande bouw": "existing",
	nieuwbouw: "new",
};

const transformBuildType = (value: unknown) =>
	typeof value === "string" ? mapBuildTypeToValue[value] : undefined;
const transformObjectType = (value: unknown) =>
	typeof value === "string" ? mapFundaType(value) : undefined;
const transformEnergyLabel = (value: unknown) =>
	typeof value === "string" ? mapEnergyLabelToValue[value] : undefined;

const fundaBuildType = computed(() =>
	fundaProperties.value
		?.find((p: any) => p.Id === "bouw")
		?.KenmerkenList?.find((k: any) => k.Id === "bouw-soortbouw")
		?.Value?.toLowerCase(),
);

const fundaObjectType = computed(() =>
	fundaProperties.value
		?.find((p: any) => p.Id === "bouw")
		?.KenmerkenList?.find((k: any) => k.Id === "bouw-soortobject")
		?.Value?.toLowerCase(),
);

const fundaGaragePresent = computed(() => {
	if (!fundaProperties.value) return undefined;
	return fundaProperties.value?.find((p: any) => p.Id === "garage")
		? true
		: false;
});

const sortedEnergyLabels = computed(() => {
	const labels = props.addressMeta?.energyLabel;
	if (!labels || !Array.isArray(labels) || labels.length === 0) return [];
	return [...labels].sort(
		(a: any, b: any) =>
			new Date(b.Registratiedatum).getTime() -
			new Date(a.Registratiedatum).getTime(),
	);
});

const epOnlineEnergyLabel = computed(
	() => sortedEnergyLabels.value[0]?.Energieklasse,
);

const fundaEnergyLabel = computed(
	() => mostRecentListing.value?.funda_energy_label,
);

const fundaErfpacht = computed(() => getErfpacht(fundaProperties.value)[0]);

const kadasterBouwjaar = computed(
	() =>
		props.addressMeta?.address?._embedded?.panden?.[0]?.pand
			?.oorspronkelijkBouwjaar,
);
const epOnlineBouwjaar = computed(() => sortedEnergyLabels.value[0]?.Bouwjaar);
const fundaBouwjaar = computed(
	() => mostRecentListing.value?.funda_construction_year,
);
const transformBouwjaar = (value: unknown) =>
	typeof value === "number" || typeof value === "string"
		? String(value)
		: undefined;

const epOnlineRegistratiedatum = computed(
	() => sortedEnergyLabels.value[0]?.Registratiedatum,
);
const transformDate = (value: unknown) =>
	typeof value === "string"
		? (fuzzyDateParse(value).toISODate() ?? undefined)
		: undefined;

function applyErfpacht(erfpacht: Erfpacht) {
	if (erfpacht) {
		dossierDataStore.data.objectData.erfpacht = { ...erfpacht };
		const canonYearly = erfpacht.canonYearly;
		if (canonYearly) {
			dossierDataStore.data.objectData.erfpacht.canonYearly = canonYearly;
		}
	}
}

function applyWozFromAddressMeta(wozWaarde?: number) {
	if (wozWaarde !== undefined) {
		dossierDataStore.data.objectData.wozWaarde = wozWaarde;
	}
	const peildatum = props.addressMeta?.wozWaarden?.[0]?.peildatum;
	if (peildatum) {
		dossierDataStore.data.objectData.wozWaardeTaxatieDate =
			transformDate(peildatum);
	}
}

function applyWozToMarktwaarde(wozWaarde?: number) {
	if (wozWaarde === undefined) {
		return;
	}
	dossierDataStore.data.objectData.marktwaarde = wozWaarde;
	dossierDataStore.data.objectData.bronWaardebepaling =
		BronWaardebepalingType["02 WOZ"];
}

const marktwaardeDataSources = computed<DataSource[]>(() => {
	const wozWaarde = props.addressMeta?.wozWaarden?.[0]?.vastgesteldeWaarde;
	if (wozWaarde === undefined || wozWaarde === null) {
		return [];
	}
	return [
		{
			value: wozWaarde,
			source: "wozWaardeLoket",
			onApply: applyWozToMarktwaarde,
			tooltip: "WOZ-waarde is een indicatie, geen taxatie",
		},
	];
});

function applyAllDataSources() {
	const fundaKoopsom = mostRecentListing.value?.funda_price_num;
	if (fundaKoopsom) {
		dossierDataStore.data.financieringsOpzet.aankoopkosten ??= {};
		dossierDataStore.data.financieringsOpzet.aankoopkosten.koopsom =
			fundaKoopsom;
	}

	if (fundaBuildType.value) {
		dossierDataStore.data.objectData.buildType =
			mapBuildTypeToValue[fundaBuildType.value];
	}

	if (fundaObjectType.value) {
		const mappedType = mapFundaType(fundaObjectType.value);
		if (mappedType) {
			dossierDataStore.data.objectData.objectType = mappedType;
		}
	}

	if (fundaGaragePresent.value !== undefined) {
		dossierDataStore.data.objectData.garagePresent = fundaGaragePresent.value;
	}

	const bouwjaar =
		kadasterBouwjaar.value ?? epOnlineBouwjaar.value ?? fundaBouwjaar.value;
	if (bouwjaar !== undefined) {
		dossierDataStore.data.objectData.bouwjaar = String(bouwjaar);
	}

	if (
		epOnlineEnergyLabel.value &&
		epOnlineEnergyLabel.value in mapEnergyLabelToValue
	) {
		dossierDataStore.data.objectData.energielabel =
			mapEnergyLabelToValue[epOnlineEnergyLabel.value];
	} else if (
		fundaEnergyLabel.value &&
		fundaEnergyLabel.value in mapEnergyLabelToValue
	) {
		dossierDataStore.data.objectData.energielabel =
			mapEnergyLabelToValue[fundaEnergyLabel.value];
	}

	if (epOnlineRegistratiedatum.value) {
		dossierDataStore.data.objectData.energielabelAfgifteDate = transformDate(
			epOnlineRegistratiedatum.value,
		);
	}

	if (fundaErfpacht.value) {
		applyErfpacht(fundaErfpacht.value);
	}
}

const fundaImage = computed(() => {
	if (!mostRecentListing.value) {
		return null;
	}

	const ids = JSON.parse(mostRecentListing.value.funda_image_ids);

	const firstEntry = ids[0];
	if (!firstEntry) {
		return null;
	}

	if (typeof firstEntry === "string") {
		return firstEntry;
	}

	return firstEntry.id;
});

const imageUrl = computed(() => {
	if (fundaImage.value) {
		return `https://cloud.funda.nl/valentina_media/${fundaImage.value}.jpg`;
	}

	if (props.addressMeta) {
		return `https://maps.googleapis.com/maps/api/streetview?size=640x427&location=${addressLine5(address.value)} ${addressLine6(address.value)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`;
	}

	return null;
});

const hasSomeSources = computed(() => {
	return (
		Boolean(props.addressMeta?.address) ||
		Boolean(mostRecentListing.value) ||
		Boolean(sortedEnergyLabels.value.length > 0) ||
		Boolean(props.addressMeta?.wozWaarden)
	);
});

watch(
	() => props.addressMeta?.wozWaarden,
	(vals) => {
		if (vals?.length > 0) {
			dossierDataStore.data.objectData.wozWaarde = vals[0].vastgesteldeWaarde;
			dossierDataStore.data.objectData.wozWaardeTaxatieDate =
				fuzzyDateParse(vals[0].peildatum).toISODate() ?? undefined;
		}
	},
	{ immediate: true },
);
</script>
